/**
 * Zajednička, čista (pure) logika odbrojavanja ispitnog timera.
 *
 * VAŽNO: Ova datoteka mora biti BAJT-IDENTIČNA istoimenoj datoteci u player
 * aplikaciji (`Performa365ExamsStudent/src/components/exam/examTimer.ts`).
 * Time je zajamčeno da polaznik (player) i administrator (monitoring) odbrojavaju
 * vrijeme po POTPUNO ISTOJ formuli. Oboje se sidre na apsolutni "zidni" rok
 * izveden iz iste autoritativne serverske vrijednosti (preostale sekunde iz
 * `ComputeExamInstanceSnapshotAsync`), pa prikaz nije podložan driftu intervala.
 */

/**
 * Sidri apsolutni rok (epoch ms) na temelju autoritativne serverske vrijednosti
 * preostalih sekundi. Negativne/nevažeće ulaze tretira kao 0.
 *
 * @param serverRemainingSeconds Preostale sekunde koje je izračunao server.
 * @param nowMs Trenutno "zidno" vrijeme u epoch ms (default: Date.now()).
 */
export function anchorDeadlineMs(
  serverRemainingSeconds: number,
  nowMs: number = Date.now()
): number {
  const safeRemaining = Number.isFinite(serverRemainingSeconds)
    ? Math.max(0, serverRemainingSeconds)
    : 0;
  return nowMs + safeRemaining * 1000;
}

/**
 * Računa preostale cijele sekunde iz prethodno usidrenog roka i trenutnog
 * "zidnog" vremena. Otporno na drift intervala (spor render, opterećen CPU).
 *
 * @param deadlineMs Apsolutni rok u epoch ms (vidi anchorDeadlineMs).
 * @param nowMs Trenutno "zidno" vrijeme u epoch ms (default: Date.now()).
 */
export function computeRemainingSeconds(
  deadlineMs: number,
  nowMs: number = Date.now()
): number {
  return Math.max(0, Math.round((deadlineMs - nowMs) / 1000));
}

/**
 * Odlučuje treba li ponovno usidriti rok kad stigne svježa serverska vrijednost.
 * Re-sidrimo samo kod značajne razlike (> tolerancije) da izbjegnemo trzanje
 * prikaza zbog mrežnog kašnjenja od ±1 s.
 *
 * @param currentDeadlineMs Trenutno usidreni rok (ili null ako još nije usidren).
 * @param serverRemainingSeconds Svježa serverska vrijednost preostalih sekundi.
 * @param toleranceMs Dozvoljeno odstupanje prije re-sidrenja (default 2000 ms).
 * @param nowMs Trenutno "zidno" vrijeme u epoch ms (default: Date.now()).
 */
export function shouldReanchor(
  currentDeadlineMs: number | null,
  serverRemainingSeconds: number,
  toleranceMs: number = 2000,
  nowMs: number = Date.now()
): boolean {
  if (currentDeadlineMs === null) {
    return true;
  }
  const newDeadline = anchorDeadlineMs(serverRemainingSeconds, nowMs);
  return Math.abs(newDeadline - currentDeadlineMs) > toleranceMs;
}

// ===========================================================================
// NAJROBUSNIJI PRISTUP: offset sata (Cristian) + monotoni sat (performance.now)
// ===========================================================================
//
// Backend šalje autoritativni snapshot (ExamTimerSyncViewModel):
//   - serverNowUnixMs  (epoch ms, UTC, u trenutku izračuna)
//   - endsAtUnixMs     (epoch ms; postavljen SAMO dok timer teče)
//   - remainingSeconds (zamrznuta vrijednost dok je pauza / fallback)
//   - isRunning        (teče li ili je zamrznut)
//
// Klijent:
//   1) procijeni offset sata klijent↔server (Cristian's algorithm),
//   2) iz endsAt + offset izračuna PRECIZNO preostalo vrijeme u trenutku primitka
//      (uzimajući u obzir mrežno kašnjenje),
//   3) odbrojava MONOTONIM satom (performance.now()) koji je imun na skokove
//      sistemskog sata (NTP/DST), za razliku od Date.now().

/** Autoritativni snapshot timera sa servera. */
export interface ServerTimerSnapshot {
  examInstanceId: string;
  serverNowUnixMs: number;
  remainingSeconds: number | null;
  endsAtUnixMs: number | null;
  isRunning: boolean;
  state: number;
}

/** Procijenjeni offset sata: serverClock − clientClock (ms) + izmjereni RTT. */
export interface ClockOffset {
  offsetMs: number;
  rttMs: number;
}

/**
 * Cristian's algorithm: procjenjuje offset sata klijent↔server uzimanjem više
 * uzoraka i odabirom onoga s NAJKRAĆIM round-tripom (najmanja pogreška).
 *
 * @param fetchServerNowUnixMs Dohvati serversko vrijeme (epoch ms).
 * @param samples Broj uzoraka (default 5).
 * @param nowFn Izvor "zidnog" vremena (default Date.now) - injektabilno radi testova.
 */
export async function estimateClockOffset(
  fetchServerNowUnixMs: () => Promise<number>,
  samples: number = 5,
  nowFn: () => number = Date.now
): Promise<ClockOffset> {
  let best: ClockOffset | null = null;
  for (let i = 0; i < samples; i++) {
    const t0 = nowFn();
    let serverMs: number;
    try {
      serverMs = await fetchServerNowUnixMs();
    } catch {
      continue;
    }
    const t1 = nowFn();
    const rtt = t1 - t0;
    const offset = serverMs + rtt / 2 - t1;
    if (best === null || rtt < best.rttMs) {
      best = { offsetMs: offset, rttMs: rtt };
    }
  }
  return best ?? { offsetMs: 0, rttMs: 0 };
}

/** Monotono sidro: preostale ms i performance.now() u trenutku sidrenja. */
export interface MonotonicAnchor {
  remainingMsAtAnchor: number;
  perfAtAnchorMs: number;
  running: boolean;
}

/**
 * Precizno preostalo vrijeme (ms) u trenutku primitka snapshota.
 * Dok teče: iz endsAt i procijenjenog serverskog "sada" (offset). Inače: iz
 * remainingSeconds (zamrznuto). Vraća null kad nema vremenskog ograničenja.
 */
export function remainingMsAtReceipt(
  snap: ServerTimerSnapshot,
  clockOffsetMs: number,
  nowClientMs: number = Date.now()
): number | null {
  if (snap.isRunning && snap.endsAtUnixMs != null) {
    const serverNowEst = nowClientMs + clockOffsetMs;
    return Math.max(0, snap.endsAtUnixMs - serverNowEst);
  }
  if (snap.remainingSeconds != null) {
    return Math.max(0, snap.remainingSeconds * 1000);
  }
  return null;
}

/**
 * Sidri monotoni countdown iz serverskog snapshota. Vraća null kad nema
 * vremenskog ograničenja.
 */
export function anchorMonotonic(
  snap: ServerTimerSnapshot,
  clockOffsetMs: number,
  perfNowMs: number = performance.now(),
  nowClientMs: number = Date.now()
): MonotonicAnchor | null {
  const remMs = remainingMsAtReceipt(snap, clockOffsetMs, nowClientMs);
  if (remMs === null) {
    return null;
  }
  return {
    remainingMsAtAnchor: remMs,
    perfAtAnchorMs: perfNowMs,
    running: snap.isRunning,
  };
}

/**
 * Preostale cijele sekunde iz monotonog sidra. Dok je zamrznuto (pauza) vraća
 * usidrenu vrijednost bez odbrojavanja; dok teče oduzima proteklo monotono vrijeme.
 */
export function remainingSecondsMonotonic(
  anchor: MonotonicAnchor,
  perfNowMs: number = performance.now()
): number {
  if (!anchor.running) {
    return Math.max(0, Math.round(anchor.remainingMsAtAnchor / 1000));
  }
  const elapsedMs = perfNowMs - anchor.perfAtAnchorMs;
  return Math.max(0, Math.round((anchor.remainingMsAtAnchor - elapsedMs) / 1000));
}

/**
 * Gradi fallback snapshot iz pukih "preostalo sekundi" + stanja, kad nemamo puni
 * serverski snapshot (npr. stariji backend, ili djelomičan odgovor). Bez endsAt,
 * pa se odbrojavanje oslanja na remainingSeconds (i dalje monotono).
 */
export function fallbackSnapshot(
  examInstanceId: string,
  remainingSeconds: number | null,
  isRunning: boolean,
  state: number = 0,
  nowClientMs: number = Date.now()
): ServerTimerSnapshot {
  return {
    examInstanceId,
    serverNowUnixMs: nowClientMs,
    remainingSeconds,
    endsAtUnixMs: null,
    isRunning,
    state,
  };
}
